import { Component, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { finalize, Subscription } from 'rxjs';
import { ProductionInventoryService } from '../../services/production-inventory.service';
import { CompanyFactoryService } from '../../services/company-factory.service';
import { UserRole } from '../../../employees/employees.model';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ProductService } from '../../../products/product.service';
import { ProductNameList } from '../../../products/product.model';
import {
  BomConsumptionMaterial,
  BomConsumptionResponse,
  Company,
  Factory,
  InsufficientStockError,
  PRODUCTION_ENTRY_TRANSFERRED_ERROR,
  ProductStockAdjustmentContext,
  ProductionEntry,
  ProductionEntryDeleteError,
  ProductionEntryReqBody,
  ProductionTab,
  StockAdjustmentScope,
  RawMaterialConsumption,
  RawMaterialConsumptionRequest,
} from '../../production-inventory.model';
import { ERROR_MESSAGES } from '../../../common/helpers/constants';
import { PaginatedResponse, TableColumn, Action } from '../../../common/models/common.model';
import { RawMaterialService } from '../../../raw-material-inventory/raw-material.service';
import { RawMaterialItem, RawMaterialWarehouse } from '../../../raw-material-inventory/raw-material.model';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../../../authentication/auth.service';
import { User } from '../../../authentication/auth.models';
import { SnackbarService } from '../../../services/snackbar.service';
import { formatDecimalQuantity, toNumberOrNull } from '../../../utils';
import { DeleteConfirmationModalComponent } from '../../../common/components/delete-confirmation-modal/delete-confirmation-modal.component';

interface BomRowState {
  qty: number;
  isLogging: boolean;
}

@Component({
  selector: 'app-production-entries',
  standalone: false,
  templateUrl: './production-entries.component.html',
  styleUrl: './production-entries.component.scss',
})
export class ProductionEntriesComponent implements OnInit, OnDestroy {
  @Input() factoryList: Factory[] = [];
  @ViewChild('viewProductionModal') viewProductionModalRef!: TemplateRef<any>;
  @ViewChild('exportModal') exportModalRef!: TemplateRef<any>;

  protected companyList: Company[] = [];

  protected productionEntries: ProductionEntry[] = [];
  protected isDataLoading: boolean = false;
  protected selectedEntry?: ProductionEntry;

  private adjustModalRef: NgbModalRef | null = null;
  private subscriptions: Subscription = new Subscription();
  private productionInventorySubscription!: Subscription;
  protected currentPage: number = 1;
  protected modalRef: NgbModalRef | null = null;
  protected productNameList: ProductNameList[] = [];
  protected rawMaterialList: RawMaterialItem[] = [];
  protected warehouses: RawMaterialWarehouse[] = [];

  protected formError: any = null;
  protected consumptionForm!: FormGroup;
  protected consumptionError: string | InsufficientStockError | null = null;
  protected isAddingConsumption = false;
  protected deletingConsumptionId: string | null = null;

  protected totalCount: number = 0;
  protected hasNextPage = false;
  protected hasPreviousPage = false;
  protected currentUser: User | null = null;

  /**
   * Deleting is admin-only server-side (DRF IsAdminUser, which reads `is_staff`). Module
   * permissions are switched off and carry no code for this action, so a permission grant
   * would still come back 403; `is_staff` is the only signal that matches the backend.
   */
  protected deleteError: string | null = null;
  protected isDeletingEntry = false;

  /**
   * Set when a delete is refused because transfers still reference the batch. The server
   * hands back the blocking ids, which turns that refusal into something the admin can
   * act on: clear the transfers, then come back and delete the entry.
   */
  protected blockedByTransfers: { entry: ProductionEntry; transferIds: string[] } | null = null;

  /**
   * /api/factory-batch-stocks/ hides any batch at zero, so a batch wrongly zeroed cannot
   * be corrected from the Factory Stock tab. Every batch is listed here regardless of
   * balance, which makes this the way back up.
   */
  protected adjustContext: ProductStockAdjustmentContext | null = null;
  protected historyEntry: ProductionEntry | null = null;
  protected historyScope: StockAdjustmentScope = 'factory';
  protected historyRefreshToken = 0;

  protected selectedFactory: string | null = null;
  protected selectedProduct: string | null = null;
  protected fromDate: string = '';
  protected toDate: string = '';

  protected exportFromDate: string = '';
  protected exportToDate: string = '';
  protected exportFactory: string | null = null;
  protected exportProduct: string | null = null;
  protected exportCompany: string | null = null;
  protected isExporting: boolean = false;
  protected exportError: any = null;

  protected bomResponse: BomConsumptionResponse | null = null;
  protected isBomLoading = false;
  protected bomRowStates: Record<string, BomRowState> = {};
  protected bomWarehouseId: string | null = null;
  protected extraWarehouseId: string | null = null;

  protected readonly columns: TableColumn<ProductionEntry>[] = [
    {
      label: 'Date',
      field: 'production_date',
      format: val => (val ? new Date(val).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' }) : '-'),
    },
    { label: 'Production No.', field: 'production_number' },
    { label: 'Company', field: 'company_name' },
    { label: 'Factory', field: 'factory_name' },
    { label: 'Product', field: 'product_name' },
    { label: 'Batch', field: 'batch_number' },
    {
      label: 'MFG',
      field: 'manufacturing_date',
      format: val => (val ? new Date(val).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' }) : '-'),
    },
    {
      label: 'EXP',
      field: 'expiry_date',
      format: val => (val ? new Date(val).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' }) : '-'),
    },
    {
      label: 'Packaging/Type',
      field: 'packing_size',
      format: (val, row) => `${row.packing_size} ${row.packaging_unit || ''} / ${row.packaging_type || ''}`,
    },
    { label: 'Box Produced', field: 'box_produced' },
    { label: 'Units', field: 'units' },
    { label: 'Consumption', btn_type: 'consumption' },
  ];

  protected actions: Action[] = [];

  constructor(
    private productionInventoryService: ProductionInventoryService,
    private companyFactoryService: CompanyFactoryService,
    private productService: ProductService,
    private rawMaterialService: RawMaterialService,
    private modalService: NgbModal,
    private authService: AuthService,
    private snackbarService: SnackbarService,
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder
  ) {}

  ngOnInit(): void {
    this.consumptionForm = this.fb.group({
      raw_material: [null, Validators.required],
      quantity_consumed: [null, [Validators.required, Validators.min(0.001)]],
    });

    this.currentUser = this.authService.getCurrentUser();
    this.actions = [Action.DELETE];

    this.loadCompanies();
    this.getProductsList();
    this.getRawMaterialList();
    this.loadWarehouseList();
    this.getProductionEntries();
  }

  private loadCompanies(): void {
    this.companyFactoryService.listCompanies().subscribe({
      next: (companies: Company[]) => {
        this.companyList = companies;
      },
    });
  }

  private getProductsList(): void {
    this.productService.lighter_list().subscribe({
      next: (products: ProductNameList[]) => {
        this.productNameList = products;
      },
    });
  }

  private getRawMaterialList(): void {
    this.rawMaterialService.list().subscribe({
      next: (items: RawMaterialItem[]) => {
        this.rawMaterialList = items;
      },
    });
  }

  private loadWarehouseList(): void {
    this.rawMaterialService.get_warehouses().subscribe({
      next: (warehouses: RawMaterialWarehouse[]) => {
        this.warehouses = warehouses;
      },
    });
  }

  private getProductionEntries(): void {
    this.isDataLoading = true;

    const params: any = {
      page: this.currentPage,
    };

    if (this.selectedFactory) params['factory'] = this.selectedFactory;
    if (this.selectedProduct) params['product'] = this.selectedProduct;
    if (this.fromDate) params['from_date'] = this.fromDate;
    if (this.toDate) params['to_date'] = this.toDate;

    this.productionInventoryService
      .get(params)
      .pipe(finalize(() => (this.isDataLoading = false)))
      .subscribe((productionEntries: PaginatedResponse<ProductionEntry>) => {
        this.totalCount = productionEntries.count;
        this.hasNextPage = !!productionEntries.next;
        this.hasPreviousPage = !!productionEntries.previous;
        this.productionEntries = productionEntries.results || [];
      });
  }

  protected onFilterChange(): void {
    this.currentPage = 1;
    this.getProductionEntries();
  }

  protected onPageChange(page: number): void {
    this.currentPage = page;
    this.getProductionEntries();
  }

  /**
   * Delete is the only way to correct a production entry (PUT and PATCH answer 405 by
   * design), so admins reach for this routinely. The dialog names the batch and both
   * stock movements the backend will reverse.
   */
  protected onDeleteEntry(entry: ProductionEntry): void {
    const ref = this.modalService.open(DeleteConfirmationModalComponent, {
      backdrop: 'static',
      keyboard: false,
    });

    ref.componentInstance.title = `Delete batch ${entry.batch_number}?`;
    ref.componentInstance.message = this.buildDeleteMessage(entry);
    ref.componentInstance.warning = 'This cannot be undone.';

    ref.result.then(
      () => this.deleteEntry(entry),
      () => {} // dismissed
    );
  }

  private buildDeleteMessage(entry: ProductionEntry): string {
    const units = formatDecimalQuantity(entry.units, '');
    const produced = units ? `${units} units come` : 'The units produced come';
    const factory = entry.factory_name || 'the factory';

    return (
      `${produced} out of factory stock at ${factory}, and the raw material consumed by ` +
      `this batch goes back to the warehouse it came from.`
    );
  }

  private deleteEntry(entry: ProductionEntry): void {
    this.deleteError = null;
    this.blockedByTransfers = null;
    this.isDeletingEntry = true;

    this.productionInventoryService
      .deleteProductionEntry(entry.id)
      .pipe(finalize(() => (this.isDeletingEntry = false)))
      .subscribe({
        next: () => {
          this.snackbarService.show(`Batch ${entry.batch_number} deleted and its stock movements reversed.`);

          if (this.selectedEntry?.id === entry.id) {
            this.selectedEntry = undefined;
            this.modalRef?.close();
          }

          // Deleting the only row on a later page would otherwise land on a blank page.
          if (this.productionEntries.length === 1 && this.currentPage > 1) {
            this.currentPage -= 1;
          }

          this.getProductionEntries();
        },
        error: (error: any) => {
          this.deleteError = this.parseDeleteError(error);
          this.blockedByTransfers = this.parseBlockingTransfers(error, entry);
        },
      });
  }

  /**
   * The three 400 cases each tell the admin something different and actionable, so the
   * server's `detail` is shown verbatim rather than a generic failure line. Nothing is
   * written on a 400, so the delete is safe to retry once the blocker is cleared.
   */
  private parseDeleteError(error: any): string {
    const body: Partial<ProductionEntryDeleteError> = error?.error ?? {};
    const detail = typeof body.detail === 'string' ? body.detail.trim() : '';

    if (error?.status === 400 && detail) return detail;
    if (error?.status === 403) return detail || ERROR_MESSAGES['403'];
    if (error?.status === 500) return ERROR_MESSAGES['500'];

    return detail || ERROR_MESSAGES.DEFAULT;
  }

  private parseBlockingTransfers(
    error: any,
    entry: ProductionEntry
  ): { entry: ProductionEntry; transferIds: string[] } | null {
    const body: Partial<ProductionEntryDeleteError> = error?.error ?? {};
    if (error?.status !== 400 || body.error !== PRODUCTION_ENTRY_TRANSFERRED_ERROR) return null;

    const transferIds = (body.transfers ?? []).filter(id => !!id);
    return transferIds.length ? { entry, transferIds } : null;
  }

  /**
   * Sends the admin to the Transfers tab to clear the blockers. The transfers list filters
   * on factory but not on batch, so the factory narrows it server-side and the ids mark
   * the exact rows once there.
   */
  protected openBlockingTransfers(): void {
    if (!this.blockedByTransfers) return;

    const { entry, transferIds } = this.blockedByTransfers;

    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        tab: ProductionTab.PRODUCT_TRANSFERS,
        factory: entry.factory,
        batch: entry.batch_number,
        transfers: transferIds.join(','),
      },
      queryParamsHandling: 'merge',
    });
  }

  protected openAdjustModal(entry: ProductionEntry, scope: StockAdjustmentScope, content: TemplateRef<any>): void {
    const isFactory = scope === 'factory';

    this.adjustContext = {
      scope,
      production_entry: entry.id,
      batch_number: entry.batch_number,
      product_name: entry.product_name,
      packaging_label: `${entry.packing_size} ${entry.packaging_unit || ''} / ${entry.packaging_type || ''}`.trim(),
      // A batch holds factory stock only at the factory that produced it, so that is
      // known. It can sit in any number of warehouses, so the form has to ask.
      container_id: isFactory ? entry.factory : null,
      container_name: isFactory ? entry.factory_name : null,
      warehouseOptions: isFactory ? undefined : this.warehouses,
      nos: this.deriveNos(entry),
      unit: entry.packaging_unit,
      // The entry carries what was produced, not what is left. The current balances live
      // on the stock endpoints, and the batch may be missing from them entirely, so the
      // form runs without them rather than showing a figure that could be wrong.
      batch_units_available: null,
      container_units_available: null,
    };

    this.adjustModalRef = this.modalService.open(content, { size: 'lg', backdrop: 'static' });
  }

  /**
   * Units per box, which the entry does not carry directly. The backend computes produced
   * units as nos * box_produced, so dividing back out recovers it. Falls back to 0, which
   * puts the form into units-only entry rather than guessing.
   */
  private deriveNos(entry: ProductionEntry): number {
    const units = toNumberOrNull(entry.units);
    const boxes = toNumberOrNull(entry.box_produced);
    if (units === null || boxes === null || boxes <= 0) return 0;

    const nos = Math.round(units / boxes);
    return Number.isFinite(nos) && nos > 0 ? nos : 0;
  }

  protected onAdjustmentSaved(): void {
    this.adjustModalRef?.close();
    this.adjustModalRef = null;
    this.adjustContext = null;
    this.historyRefreshToken += 1;

    // The entry list itself is unchanged, but the batch's factory stock is not, and the
    // Factory Stock tab refetches on its own when it is next opened.
    this.snackbarService.show('Stock updated. The stock tabs will show the new balance when next opened.');
  }

  protected onAdjustCancelled(): void {
    this.adjustModalRef?.dismiss();
    this.adjustModalRef = null;
    this.adjustContext = null;
  }

  protected openHistoryModal(entry: ProductionEntry, scope: StockAdjustmentScope, content: TemplateRef<any>): void {
    this.historyEntry = entry;
    this.historyScope = scope;
    this.modalService.open(content, { size: 'xl' });
  }

  protected openProductionEntryFormModal(content: TemplateRef<any>): void {
    const modalOptions: NgbModalOptions = {
      size: 'lg',
      backdrop: 'static',
    };

    this.formError = null;
    this.modalRef = this.modalService.open(content, modalOptions);
  }

  protected onSaveProductionEntry(productionData: ProductionEntryReqBody): void {
    this.productionInventoryService.post(productionData).subscribe({
      next: () => {
        this.formError = null;
        this.modalRef?.close();
        this.getProductionEntries();
      },
      error: (error: any) => {
        if (error?.status === 400) {
          this.formError = ERROR_MESSAGES['400'];
        } else if (error?.status === 500) {
          this.formError = ERROR_MESSAGES['500'];
        } else {
          this.formError = ERROR_MESSAGES.DEFAULT;
        }
      },
    });
  }

  protected openViewModal(content: TemplateRef<any>, entry: ProductionEntry): void {
    this.selectedEntry = { ...entry, raw_material_consumptions: entry.raw_material_consumptions ?? [] };
    this.consumptionError = null;
    this.deleteError = null;
    this.blockedByTransfers = null;
    this.consumptionForm?.reset();
    this.bomResponse = null;
    this.bomRowStates = {};
    this.bomWarehouseId = null;
    this.extraWarehouseId = null;
    this.isBomLoading = true;

    this.productionInventoryService
      .getBomConsumption(entry.id)
      .pipe(finalize(() => (this.isBomLoading = false)))
      .subscribe({
        next: (response: BomConsumptionResponse) => {
          this.bomResponse = response;
          this.initBomRowStates();
        },
        error: () => {
          this.bomResponse = null;
        },
      });

    this.modalRef = this.modalService.open(content, { size: 'lg', backdrop: 'static' });
  }

  private initBomRowStates(): void {
    this.bomRowStates = {};
    for (const mat of this.bomResponse?.materials ?? []) {
      this.bomRowStates[mat.raw_material] = {
        qty: mat.quantity_required,
        isLogging: false,
      };
    }

    // Auto-select warehouse if any BOM material was already logged
    if (!this.bomWarehouseId) {
      const alreadyLogged = this.bomResponse?.materials?.find(mat => this.isBomMaterialLogged(mat.raw_material));
      if (alreadyLogged) {
        const consumption = this.selectedEntry?.raw_material_consumptions.find(
          c => c.raw_material === alreadyLogged.raw_material
        );
        if (consumption?.warehouse) {
          this.bomWarehouseId = consumption.warehouse;
        }
      }
    }
  }

  protected isBomMaterialLogged(rawMaterialId: string): boolean {
    return this.selectedEntry?.raw_material_consumptions.some(c => c.raw_material === rawMaterialId) ?? false;
  }

  protected get isBomWarehouseLocked(): boolean {
    return !!this.bomResponse?.materials?.some(mat => this.isBomMaterialLogged(mat.raw_material));
  }

  protected getBomRowState(rawMaterialId: string): BomRowState {
    return this.bomRowStates[rawMaterialId];
  }

  protected onLogBomConsumption(mat: BomConsumptionMaterial): void {
    if (!this.selectedEntry || !this.bomWarehouseId) return;
    const state = this.bomRowStates[mat.raw_material];
    if (!state?.qty) return;

    state.isLogging = true;
    this.consumptionError = null;

    const reqBody: RawMaterialConsumptionRequest = {
      production_entry: this.selectedEntry.id,
      raw_material: mat.raw_material,
      quantity_consumed: state.qty,
      warehouse: this.bomWarehouseId,
    };

    this.productionInventoryService
      .postConsumption(reqBody)
      .pipe(finalize(() => (state.isLogging = false)))
      .subscribe({
        next: (consumption: RawMaterialConsumption) => {
          this.selectedEntry!.raw_material_consumptions.push(consumption);
          this.syncSelectedEntryToList();
        },
        error: (error: any) => {
          this.consumptionError = this.parseConsumptionError(error);
        },
      });
  }

  protected onAddConsumption(): void {
    if (!this.selectedEntry || this.consumptionForm.invalid || !this.extraWarehouseId) return;

    this.isAddingConsumption = true;
    this.consumptionError = null;

    const rawMaterial = this.consumptionForm.get('raw_material')!.value;
    const reqBody: RawMaterialConsumptionRequest = {
      production_entry: this.selectedEntry.id,
      raw_material: rawMaterial?.id ?? rawMaterial,
      quantity_consumed: this.consumptionForm.get('quantity_consumed')!.value,
      warehouse: this.extraWarehouseId,
    };

    this.productionInventoryService
      .postConsumption(reqBody)
      .pipe(finalize(() => (this.isAddingConsumption = false)))
      .subscribe({
        next: (consumption: RawMaterialConsumption) => {
          this.selectedEntry!.raw_material_consumptions.push(consumption);
          this.consumptionForm.reset();
          this.syncSelectedEntryToList();
        },
        error: (error: any) => {
          this.consumptionError = this.parseConsumptionError(error);
        },
      });
  }

  private parseConsumptionError(error: any): string | InsufficientStockError {
    const body = error?.error;
    if (body?.raw_material && body?.available !== undefined && body?.shortage !== undefined) {
      return body as InsufficientStockError;
    } else if (error?.status === 400) {
      const detail: string = body?.detail || body?.non_field_errors?.[0] || '';
      if (detail.toLowerCase().includes('stock record') || detail.toLowerCase().includes('no stock')) {
        return `No stock record found for this material in the selected warehouse. Ensure stock has been received at this warehouse before logging consumption.`;
      }
      if (detail.toLowerCase().includes('unique set') || detail.toLowerCase().includes('already exists')) {
        return `This raw material has already been logged for this production entry.`;
      }
      return detail || ERROR_MESSAGES['400'];
    } else if (error?.status === 500) {
      return ERROR_MESSAGES['500'];
    }
    return ERROR_MESSAGES.DEFAULT;
  }

  protected onDeleteConsumption(id: string): void {
    this.deletingConsumptionId = id;
    this.consumptionError = null;

    this.productionInventoryService
      .deleteConsumption(id)
      .pipe(finalize(() => (this.deletingConsumptionId = null)))
      .subscribe({
        next: () => {
          this.selectedEntry!.raw_material_consumptions = this.selectedEntry!.raw_material_consumptions.filter(
            c => c.id !== id
          );
          this.syncSelectedEntryToList();
        },
        error: () => {
          this.consumptionError = ERROR_MESSAGES.DEFAULT;
        },
      });
  }

  private syncSelectedEntryToList(): void {
    const idx = this.productionEntries.findIndex(e => e.id === this.selectedEntry!.id);
    if (idx !== -1) {
      this.productionEntries[idx] = { ...this.selectedEntry! };
    }
  }

  protected isInsufficientStockError(error: any): error is InsufficientStockError {
    return typeof error === 'object' && error !== null && 'raw_material' in error;
  }

  protected openExportModal(): void {
    this.exportFromDate = '';
    this.exportToDate = '';
    this.exportFactory = null;
    this.exportProduct = null;
    this.exportCompany = null;
    this.exportError = null;
    this.modalService.open(this.exportModalRef, { size: 'md' });
  }

  protected onExportSubmit(modalRef: any): void {
    this.isExporting = true;
    this.exportError = null;

    const q: Record<string, any> = {};
    if (this.exportFromDate) q['from_date'] = this.exportFromDate;
    if (this.exportToDate) q['to_date'] = this.exportToDate;
    if (this.exportFactory) q['factory'] = this.exportFactory;
    if (this.exportProduct) q['product'] = this.exportProduct;
    if (this.exportCompany) q['company'] = this.exportCompany;

    this.productionInventoryService
      .export_production(q)
      .pipe(finalize(() => (this.isExporting = false)))
      .subscribe({
        next: (blob: Blob) => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'production_entries.xlsx';
          a.click();
          URL.revokeObjectURL(url);
          modalRef.close();
        },
        error: err => {
          this.exportError = err;
        },
      });
  }

  ngOnDestroy(): void {
    this.productionInventorySubscription?.unsubscribe();
    this.subscriptions.unsubscribe();
    this.modalRef?.dismiss();
    this.adjustModalRef?.dismiss();
  }

  protected readonly UserRole = UserRole;
}
